import { useEffect, useState } from "react";
import { CategoriesAPI, SubcategoriesAPI, DepartmentsAPI } from "../../api/admin";

export default function CategoryList() {
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryDepartmentId, setNewCategoryDepartmentId] = useState("");
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState("");
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // track which categories are expanded
  const [newSub, setNewSub] = useState({}); // input values per category

  useEffect(() => {
    loadCategories();
    loadDepartments();
  }, []);

  async function loadCategories() {
    try {
      const res = await CategoriesAPI.list();
      // Optionally fetch subcategories here if API returns only categories
      setCategories(res);
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadDepartments() {
  try {
    const res = await DepartmentsAPI.list();
    setDepartments(res);
  } catch (err) {
    setError(err.message);
  }
}

  async function handleCategoryCreate(e) {
    e.preventDefault();

    if (!newCategoryDepartmentId) {
      setError("Please select a department");
      return;
    }

    try {
      await CategoriesAPI.create({
        name: newCategory,
        department_id: newCategoryDepartmentId,
      });
      setNewCategory("");
      setNewCategoryDepartmentId("");
      setError(null);
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

 function getDepartmentName(departmentId) {
    return (
      departments.find((d) => String(d.id) === String(departmentId))?.name ||
      "No department"
    );
  }

  const filteredCategories = selectedDepartmentFilter
  ? categories.filter(
      (c) => String(c.department_id) === String(selectedDepartmentFilter)
    )
  : categories;

  async function handleCategoryDelete(id) {
    if (!window.confirm("Delete this category?")) return;
    try {
      await CategoriesAPI.delete(id);
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSubCreate(categoryId) {
    try {
      await SubcategoriesAPI.create(categoryId, {
        name: newSub[categoryId] || "",
      });
      setNewSub({ ...newSub, [categoryId]: "" });
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSubDelete(categoryId, subId) {
    if (!window.confirm("Delete this subcategory?")) return;
    try {
      await SubcategoriesAPI.delete(categoryId, subId);
      await loadCategories();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Manage Categories & Subcategories</h1>
      {error && <p className="text-red-500">{error}</p>}

      {/* Add new category */}
      <form onSubmit={handleCategoryCreate} className="flex gap-2 mb-4">
        <input
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          placeholder="New category name"
          className="border rounded p-2 flex-1"
        />

        <select
          value={newCategoryDepartmentId}
          onChange={(e) => setNewCategoryDepartmentId(e.target.value)}
          className="border rounded p-2"
        >
          <option value="">-- Select Department --</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          Add
        </button>
      </form>

      <div className="mb-4 flex gap-2 items-center">
        <label className="text-sm font-medium">Filter by department:</label>
        <select
          value={selectedDepartmentFilter}
          onChange={(e) => setSelectedDepartmentFilter(e.target.value)}
          className="border rounded p-2"
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      {/* List categories */}
      <ul className="border rounded divide-y">
        {filteredCategories.length === 0 && (
          <li className="p-3 text-gray-500">No categories found for this department</li>
        )}
        {filteredCategories.map((c) => (
          <li key={c.id} className="p-2">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold">{c.name}</div>
                <div className="text-sm text-gray-500">
                  {getDepartmentName(c.department_id)}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setExpanded({ ...expanded, [c.id]: !expanded[c.id] })
                  }
                  className="text-blue-600 underline"
                >
                  {expanded[c.id] ? "Hide Subs" : "Show Subs"}
                </button>
                <button
                  onClick={() => handleCategoryDelete(c.id)}
                  className="text-red-600 underline"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Subcategories */}
            {expanded[c.id] && (
              <div className="ml-6 mt-2">
                <ul className="border rounded divide-y mb-2">
                  {c.subcategories?.map((s) => (
                    <li
                      key={s.id}
                      className="flex justify-between items-center p-2"
                    >
                      {s.name}
                      <button
                        onClick={() => handleSubDelete(c.id, s.id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                  {(!c.subcategories || c.subcategories.length === 0) && (
                    <li className="p-2 text-gray-500">No subcategories</li>
                  )}
                </ul>

                <div className="flex gap-2">
                  <input
                    value={newSub[c.id] || ""}
                    onChange={(e) =>
                      setNewSub({ ...newSub, [c.id]: e.target.value })
                    }
                    placeholder="New subcategory name"
                    className="border rounded p-2 flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => handleSubCreate(c.id)}
                    className="bg-green-600 text-white px-4 py-2 rounded"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}